(function() {
  'use strict';

  // Pure parsing/pricing/quantity-rule logic lives in recharge-build-your-own-logic.js
  // (loaded before this file) and is exposed as window.LobelsRB.Logic. This file only
  // reads/writes the DOM and talks to the Shopify cart + Recharge APIs.
  var Logic = window.LobelsRB && window.LobelsRB.Logic;
  var toInt = Logic.toInt;
  var parsePositiveInt = Logic.parsePositiveInt;
  var formatMoney = Logic.formatMoney;
  var parseBundleEditItems = Logic.parseBundleEditItems;
  var parseBundleRemoveUpdates = Logic.parseBundleRemoveUpdates;
  var parseBundleLineKeys = Logic.parseBundleLineKeys;
  var deriveVariantMeta = Logic.deriveVariantMeta;
  var parseSellingPlanPrices = Logic.parseSellingPlanPrices;
  var variantSupportsSellingPlan = Logic.variantSupportsSellingPlan;
  var computeCardQuantityRules = Logic.computeCardQuantityRules;
  var normalizeCardQuantity = Logic.normalizeCardQuantity;
  var computeSummary = Logic.computeSummary;
  var computePricingForPlan = Logic.computePricingForPlan;

  var rechargeSdkSrc = 'https://static.rechargecdn.com/assets/storefront/recharge-client-1.88.0.min.js';

  function hasUrlSelectionItems() {
    var params = new URLSearchParams(window.location.search);
    var rawContents = params.get('contents') || '';
    return parseBundleEditItems(rawContents).length > 0;
  }

  function getBundleEditContext(bundleHandle) {
    var params = new URLSearchParams(window.location.search);
    if (params.get('rc_edit') !== '1') return null;

    var context = {
      items: parseBundleEditItems(params.get('contents') || ''),
      sellingPlanId: '',
      removeUpdates: {},
      storageKey: bundleHandle ? ('rcEditBundle:' + bundleHandle) : ''
    };

    var querySellingPlanId = String(params.get('rc_selling_plan') || '').trim();
    if (querySellingPlanId && querySellingPlanId !== '0') {
      context.sellingPlanId = querySellingPlanId;
    }

    var queryLineKeys = parseBundleLineKeys(params.get('rc_line_keys') || '');
    if (queryLineKeys.length) {
      queryLineKeys.forEach(function(lineKey) {
        context.removeUpdates[lineKey] = 0;
      });
    }

    var queryOriginalBundleId = String(params.get('rc_bundle_id') || '').trim();
    if (queryOriginalBundleId) {
      context.originalBundleId = queryOriginalBundleId;
    }

    if (!context.storageKey) {
      return context;
    }

    try {
      var raw = sessionStorage.getItem(context.storageKey);
      if (!raw) return context;

      var stored = JSON.parse(raw);
      if (!context.items.length && stored && stored.contents) {
        context.items = parseBundleEditItems(stored.contents);
      }

      if (!context.sellingPlanId && stored && stored.sellingPlan) {
        var storedPlanId = String(stored.sellingPlan).trim();
        if (storedPlanId && storedPlanId !== '0') {
          context.sellingPlanId = storedPlanId;
        }
      }

      if (stored && stored.removeUrl) {
        context.removeUpdates = parseBundleRemoveUpdates(stored.removeUrl);
      }

      if (stored && stored.lineKeys) {
        parseBundleLineKeys(stored.lineKeys).forEach(function(lineKey) {
          context.removeUpdates[lineKey] = 0;
        });
      }

      if (!context.originalBundleId && stored && stored.originalBundleId) {
        context.originalBundleId = String(stored.originalBundleId || '').trim();
      }
    } catch (e) {
      // Ignore malformed storage payloads.
    }

    return context;
  }

  function getRechargeBundleMethods() {
    if (!window.recharge) return null;

    var client = window.recharge;
    var validateBundle = (client.bundle && client.bundle.validateBundle) || client.validateBundle;
    var getBundleId = (client.bundle && client.bundle.getBundleId) || client.getBundleId;

    if (typeof validateBundle !== 'function' || typeof getBundleId !== 'function') {
      return null;
    }

    return {
      client: client,
      validateBundle: validateBundle,
      getBundleId: getBundleId
    };
  }

  function loadRechargeSdk() {
    if (window.recharge) return Promise.resolve();
    if (window.__lobelsRechargeSdkPromise) return window.__lobelsRechargeSdkPromise;

    window.__lobelsRechargeSdkPromise = new Promise(function(resolve, reject) {
      var existingScript = document.querySelector('script[data-recharge-sdk="true"]');

      if (existingScript) {
        existingScript.addEventListener('load', function() { resolve(); }, { once: true });
        existingScript.addEventListener('error', function() { reject(new Error('Recharge SDK failed to load.')); }, { once: true });
        return;
      }

      var script = document.createElement('script');
      script.src = rechargeSdkSrc;
      script.async = true;
      script.dataset.rechargeSdk = 'true';
      script.onload = function() { resolve(); };
      script.onerror = function() { reject(new Error('Recharge SDK failed to load.')); };
      document.head.appendChild(script);
    });

    return window.__lobelsRechargeSdkPromise;
  }

  async function initRecharge(client, storeIdentifier) {
    if (window.__lobelsRechargeBuilderInitialized) return;
    if (!client || typeof client.init !== 'function') return;

    await client.init({
      storeIdentifier: storeIdentifier,
      appName: 'lobels-build-your-own',
      appVersion: '2.0.0'
    });

    window.__lobelsRechargeBuilderInitialized = true;
  }

  async function addBundleToCart(bundleConfig, rbId, selections, purchaseConfig, editContext) {
    var bundleHandle = String(bundleConfig.bundleHandle || '');
    var bundleVariantId = String(bundleConfig.bundleVariantId || '');
    var purchaseType = purchaseConfig && purchaseConfig.type === 'subscription' ? 'Subscribe' : 'One-time';
    var purchaseLabel = purchaseConfig && purchaseConfig.label ? String(purchaseConfig.label) : purchaseType;
    var sellingPlanId = purchaseConfig && purchaseConfig.planId ? toInt(purchaseConfig.planId, 0) : 0;
    var wantsSubscription = purchaseType === 'Subscribe' && sellingPlanId > 0;

    if (wantsSubscription) {
      var hasIneligibleVariant = selections.some(function(selection) {
        return !selection.supportsSellingPlan;
      });

      if (hasIneligibleVariant) {
        throw new Error('One or more selected variants cannot be purchased on this subscription frequency. Choose a different variant or frequency.');
      }
    }

    var editStamp = editContext && editContext.isEditMode
      ? String(editContext.replaceStamp || ('edit-' + Date.now()))
      : '';

    var items = selections.map(function(selection) {
      var item = {
        id: Number(selection.externalVariantId),
        quantity: selection.quantity,
        properties: {
          _rb_id: rbId,
          _rc_bundle: rbId,
          _rc_bundle_parent: bundleHandle,
          _rc_bundle_variant: bundleVariantId,
          'Subscription Frequency': purchaseLabel
        }
      };

      if (editStamp) {
        item.properties._rc_edit_replace = editStamp;
      }

      if (wantsSubscription) {
        item.selling_plan = sellingPlanId;
      }

      return item;
    });

    var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var response = await fetch(cartRoot + 'cart/add.js', {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: items })
    });

    if (!response.ok) {
      var errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = null;
      }

      var errorMessage = errorData && errorData.description ? errorData.description : 'Could not add bundle to cart.';
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async function removeCartLinesByKeys(removeUpdates) {
    if (!removeUpdates || !Object.keys(removeUpdates).length) return;

    var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    var response = await fetch(cartRoot + 'cart/update.js', {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates: removeUpdates })
    });

    if (!response.ok) {
      var errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = null;
      }

      throw new Error(errorData && errorData.description ? errorData.description : 'Could not remove the previous bundle items from your cart.');
    }

    var updatedCart = await response.json();

    // cart/update.js silently ignores keys it doesn't recognize (stale key, cart changed
    // elsewhere, etc.) instead of erroring, so confirm every targeted line is actually gone.
    var staleKeys = Object.keys(removeUpdates).filter(function(key) {
      return updatedCart.items.some(function(cartItem) { return cartItem.key === key; });
    });

    if (staleKeys.length) {
      throw new Error('Could not remove one or more of the previous bundle items from your cart. Please refresh and try editing again.');
    }
  }

  async function initBuilder(root) {
    if (!root || root.dataset.builderInitialized === 'true') return;
    root.dataset.builderInitialized = 'true';

    // Remove legacy count-based shipping UI if present from stale markup/cache.
    Array.prototype.forEach.call(root.querySelectorAll('[data-builder-count-label]'), function(node) {
      var wrapper = node.closest('small');
      if (wrapper) {
        wrapper.remove();
        return;
      }
      node.remove();
    });

    Array.prototype.forEach.call(root.querySelectorAll('.rb-status__delivery small, .rb-panel__delivery small'), function(node) {
      if (/\bof\b/i.test(node.textContent || '')) {
        node.remove();
      }
    });

    Array.prototype.forEach.call(root.querySelectorAll('.rb-status__fraction'), function(node) {
      node.remove();
    });

    var sourceCollectionsSessionKey = 'rbSelectedCollectionHandles';
    var hasSourceCollections = root.getAttribute('data-has-source-collections') === 'true';
    var sourceCollectionHandles = String(root.getAttribute('data-source-collection-handles') || '').trim();

    try {
      if (sourceCollectionHandles) {
        sessionStorage.setItem(sourceCollectionsSessionKey, sourceCollectionHandles);
      } else if (!hasSourceCollections) {
        var storedHandles = String(sessionStorage.getItem(sourceCollectionsSessionKey) || '').trim();
        if (storedHandles) {
          var url = new URL(window.location.href);
          if (!url.searchParams.get('rb_collections')) {
            url.searchParams.set('rb_collections', storedHandles);
            window.location.replace(url.toString());
            return;
          }
        }
      }
    } catch (e) {
      // Ignore session storage restrictions.
    }

    var submitButton = root.querySelector('[data-builder-submit]');
    var statusNode = root.querySelector('[data-builder-status]');
    var itemLabelNodes = root.querySelectorAll('[data-builder-items]');
    var panelCountNodes = root.querySelectorAll('[data-builder-panel-count]');
    var subtotalNodes = root.querySelectorAll('[data-builder-subtotal], [data-builder-panel-subtotal]');
    var remainingNode = root.querySelector('[data-builder-remaining]');
    var panelShippingCurrentNode = root.querySelector('[data-builder-panel-shipping-current]');
    var freeShippingThresholdNodes = root.querySelectorAll('[data-builder-free-shipping-threshold]');
    var topProgressCopyNode = root.querySelector('[data-builder-progress-copy]');
    var panelProgressCopyNode = root.querySelector('[data-builder-panel-progress-copy]');
    var topFillNode = root.querySelector('[data-builder-fill]');
    var panelFillNode = root.querySelector('[data-builder-panel-fill]');
    var titleNode = root.querySelector('[data-builder-title]');
    var boxItemsNode = root.querySelector('[data-builder-box-items]');
    var discountCopyNode = root.querySelector('[data-builder-discount-copy]');
    var toastNode = root.querySelector('[data-builder-toast]');
    var subscriptionPickerNode = root.querySelector('[data-builder-subscription-picker]');
    var subscriptionToggleNode = root.querySelector('[data-subscription-toggle]');
    var subscriptionCurrentNode = root.querySelector('[data-subscription-current]');
    var subscriptionMenuNode = root.querySelector('[data-subscription-menu]');
    var subscriptionFrequencyNode = root.querySelector('[data-builder-subscription-frequency]');
    var tabsNode = root.querySelector('.rb-tabs');
    var panelToggleNode = root.querySelector('[data-builder-panel-toggle]');
    var panelCloseNode = root.querySelector('[data-builder-panel-close]');
    var panelBackdropNode = root.querySelector('[data-builder-panel-backdrop]');
    var panelNode = root.querySelector('.rb-panel');
    var announcementNode = root.querySelector('[data-builder-announcement]');
    var promoFirstItemIconNode = root.querySelector('.rb-promo-details__list li:first-child .rb-promo-details__item-icon');
    var promoOpenNodes = root.querySelectorAll('[data-promo-open]');
    var promoPanelNode = root.querySelector('[data-promo-details-panel]');
    var builderIntroNode = root.querySelector('.rb-builder__intro');
    var subscribeModalNode = root.querySelector('[data-builder-subscribe-modal]');
    var subscribeModalDialogNode = root.querySelector('.rb-subscribe-modal__dialog');
    var subscribeModalOptionsNode = root.querySelector('[data-builder-subscribe-options]');
    var cardInfoModalNode = root.querySelector('[data-card-info-modal]');
    var cardInfoTitleNode = root.querySelector('[data-card-info-title]');
    var cardInfoImageWrapNode = root.querySelector('[data-card-info-image-wrap]');
    var cardInfoImageNode = root.querySelector('[data-card-info-image]');
    var cardInfoThumbsNode = root.querySelector('[data-card-info-thumbs]');
    var cardInfoDescriptionNode = root.querySelector('[data-card-info-description]');
    var cardInfoRulesNode = root.querySelector('[data-card-info-rules]');
    var cardInfoDialogNode = root.querySelector('.rb-card-info-modal__dialog');

    if (!submitButton || !statusNode || !boxItemsNode) return;

    var config = {
      minimumItems: toInt(root.dataset.minimumItems, 1),
      freeShippingThresholdCents: Math.max(0, toInt(root.dataset.freeShippingThresholdCents, 50000)),
      bundleProductId: root.dataset.bundleProductId || '',
      bundleVariantId: root.dataset.bundleVariantId || '',
      bundleHandle: root.dataset.bundleHandle || '',
      storeIdentifier: root.dataset.storeIdentifier || '',
      savingsPercent: Math.max(0, toInt(root.dataset.savingsPercent, 0)),
      moneyFormat: root.dataset.moneyFormat || '${{amount}}'
    };
    var editContext = getBundleEditContext(config.bundleHandle);
    if (editContext) {
      editContext.isEditMode = true;
      editContext.replaceStamp = 'edit-' + Date.now();
    }
    var experiment = null;
    try {
      if (window.LobelsExperiments) experiment = await window.LobelsExperiments.mount(root);
    } catch (error) {
      // Keep the existing builder usable if experimentation is unavailable.
    }
    if (!root.isConnected) return;
    var selectedPurchaseConfig = {
      type: 'one-time',
      label: 'One-time',
      planId: ''
    };
    var pendingSubmission = null;
    var subscribeModalCloseTimer = null;
    var cardInfoModalCloseTimer = null;
    var cardInfoImageLoadToken = 0;

    var toastTimer;
    var announcementTimer;
    var lastSummaryAnnouncement = null;
    var activeDialog = null;
    var modalReturnFocus = null;
    var modalInertNodes = [];

    function announce(message) {
      if (!announcementNode || !message) return;
      window.clearTimeout(announcementTimer);
      announcementNode.textContent = '';
      announcementTimer = window.setTimeout(function() {
        announcementNode.textContent = message;
      }, 180);
    }

    function focusableNodes(container) {
      return Array.prototype.filter.call(container.querySelectorAll('button, a[href], input, select, textarea, [tabindex]'), function(node) {
        return !node.disabled && node.tabIndex >= 0 && !node.closest('[inert]') && node.getClientRects().length > 0;
      });
    }

    function activateDialog(dialog, opener) {
      if (activeDialog && activeDialog !== dialog) {
        if (activeDialog === cardInfoDialogNode) closeCardInfoModal();
        else closeSubscribeModal();
      }
      activeDialog = dialog;
      modalReturnFocus = opener || document.activeElement;
      dialog.removeAttribute('inert');
      dialog.setAttribute('tabindex', '-1');
      if (announcementNode) dialog.appendChild(announcementNode);
      // Preserve pre-existing inert state while isolating the dialog from the theme.
      var branch = dialog;
      while (branch.parentElement && branch !== document.body) {
        Array.prototype.forEach.call(branch.parentElement.children, function(sibling) {
          if (sibling !== branch && !sibling.hasAttribute('inert') && !sibling.hasAttribute('data-builder-subscribe-backdrop') && !sibling.hasAttribute('data-card-info-backdrop')) {
            sibling.setAttribute('inert', '');
            modalInertNodes.push(sibling);
          }
        });
        branch = branch.parentElement;
      }
      (focusableNodes(dialog)[0] || dialog).focus({ preventScroll: true });
    }

    function deactivateDialog(dialog) {
      if (!dialog || activeDialog !== dialog) return;
      modalInertNodes.forEach(function(node) { node.removeAttribute('inert'); });
      modalInertNodes = [];
      activeDialog = null;
      if (announcementNode) root.appendChild(announcementNode);
      setPanelOpen(window.innerWidth <= 980 && root.classList.contains('is-panel-open'));
      var target = modalReturnFocus;
      if (!target || !target.isConnected || target.closest('[inert]') || !target.getClientRects().length) target = panelToggleNode;
      if (target && !target.disabled) target.focus({ preventScroll: true });
      dialog.setAttribute('inert', '');
    }

    function showToast(message) {
      if (!toastNode || !message) return;
      toastNode.textContent = message;
      toastNode.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function() {
        toastNode.classList.remove('is-visible');
      }, 1200);
    }

    function setStatus(message, isError) {
      statusNode.textContent = message || '';
      statusNode.classList.toggle('is-error', !!isError);
      announce(message);
    }

    function bindCardImageLoadingStates() {
      Array.prototype.forEach.call(root.querySelectorAll('.rb-card__media-wrap'), function(mediaWrap) {
        var cardImage = mediaWrap.querySelector('img.rb-card__img');
        if (!cardImage) {
          mediaWrap.classList.remove('is-loading');
          return;
        }

        function clearLoading() {
          mediaWrap.classList.remove('is-loading');
        }

        if (cardImage.complete) {
          clearLoading();
          return;
        }

        mediaWrap.classList.add('is-loading');
        cardImage.addEventListener('load', clearLoading, { once: true });
        cardImage.addEventListener('error', clearLoading, { once: true });
      });
    }

    function syncFirstPromoTruckIcon() {
      if (!promoFirstItemIconNode) return;
      if (promoFirstItemIconNode.dataset.promoTruckReady === 'true') return;

      var statusTruckNode = root.querySelector('.rb-status__truck');
      if (!statusTruckNode) return;

      var truckClone = statusTruckNode.cloneNode(true);
      truckClone.classList.remove('rb-status__truck');
      truckClone.classList.add('rb-promo-details__truck');
      truckClone.setAttribute('aria-hidden', 'true');

      promoFirstItemIconNode.innerHTML = '';
      promoFirstItemIconNode.appendChild(truckClone);
      promoFirstItemIconNode.dataset.promoTruckReady = 'true';
    }

    function setPanelOpen(isOpen) {
      var wasOpen = root.classList.contains('is-panel-open');
      root.classList.toggle('is-panel-open', !!isOpen);

      if (panelToggleNode) {
        panelToggleNode.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      if (panelBackdropNode) {
        panelBackdropNode.hidden = !isOpen;
      }
      if (panelNode) {
        var isMobile = window.innerWidth <= 980;
        if (!isOpen && isMobile && panelNode.contains(document.activeElement) && panelToggleNode) panelToggleNode.focus({ preventScroll: true });
        panelNode.toggleAttribute('inert', isMobile && !isOpen);
        if (isMobile && !isOpen) panelNode.setAttribute('aria-hidden', 'true');
        else panelNode.removeAttribute('aria-hidden');
        if (isOpen && !wasOpen && isMobile && panelCloseNode) panelCloseNode.focus({ preventScroll: true });
        if (announcementNode && !activeDialog) (isMobile && isOpen ? panelNode : root).appendChild(announcementNode);
      }
    }

    var promoIntroTimer = null;
    var promoIntroReady = false;

    function cancelPromoIntro() {
      window.clearTimeout(promoIntroTimer);
      promoIntroTimer = null;
      root.classList.remove('is-promo-details-intro');
      root.classList.remove('is-promo-details-revealing');
    }

    function setPromoDetailsOpen(isOpen) {
      cancelPromoIntro();
      root.classList.toggle('is-promo-details-open', !!isOpen);

      if (promoOpenNodes && promoOpenNodes.length) {
        Array.prototype.forEach.call(promoOpenNodes, function(node) {
          node.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      }

      if (promoPanelNode) {
        promoPanelNode.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        promoPanelNode.toggleAttribute('inert', !isOpen);
      }
      if (!isOpen) startPromoIntro(12000);
    }

    function shouldAnimatePromoDetails() {
      if (!(promoOpenNodes && promoOpenNodes.length) || !promoPanelNode) return false;
      if (promoPanelNode.closest('[hidden]')) return false;
      if (experiment && experiment.behavior === 'none') return false;

      if (!window.matchMedia ||
          window.matchMedia('(max-width: 1320px)').matches ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return false;
      }

      return !(editContext && editContext.isEditMode);
    }

    function canShinePromoDetails() {
      return promoIntroReady && root.isConnected && !document.hidden &&
        shouldAnimatePromoDetails() && !root.classList.contains('is-promo-details-open') &&
        !Array.prototype.some.call(promoOpenNodes, function(node) {
          return node.matches(':focus-visible');
        });
    }

    function finishPromoIntro() {
      if (!root.classList.contains('is-promo-details-intro')) return;
      cancelPromoIntro();
      startPromoIntro(11000);
    }

    function startPromoIntro(delay, reveal) {
      if (promoIntroTimer !== null || !canShinePromoDetails()) return;

      promoIntroTimer = window.setTimeout(function() {
        promoIntroTimer = null;
        if (!canShinePromoDetails()) return;
        // A visual hint only: keep the panel collapsed until the shopper opens it.
        root.classList.add('is-promo-details-intro');
        if (reveal) root.classList.add('is-promo-details-revealing');
        // Fallback if the animation ends without an animationend event.
        promoIntroTimer = window.setTimeout(finishPromoIntro, reveal ? 1400 : 1100);
      }, delay);
    }

    function closeCardInfoModal() {
      deactivateDialog(cardInfoDialogNode);
      root.classList.remove('is-card-info-modal-open');
      if (!cardInfoModalNode) return;

      cardInfoModalNode.classList.remove('is-open');

      if (cardInfoModalCloseTimer) {
        clearTimeout(cardInfoModalCloseTimer);
      }

      cardInfoModalCloseTimer = window.setTimeout(function() {
        cardInfoModalNode.hidden = true;
      }, 260);
    }

    function getCardInfoImageUrls(card) {
      if (!card) return [];

      var imageUrls = [];
      var imageMap = {};
      var primaryImage = String(card.getAttribute('data-card-image') || '').trim();
      var galleryRaw = String(card.getAttribute('data-card-images') || '').trim();

      function pushUnique(url) {
        var cleanUrl = String(url || '').trim();
        if (!cleanUrl || imageMap[cleanUrl]) return;
        imageMap[cleanUrl] = true;
        imageUrls.push(cleanUrl);
      }

      if (primaryImage) {
        pushUnique(primaryImage);
      }

      if (galleryRaw) {
        galleryRaw.split('||').forEach(function(url) {
          pushUnique(url);
        });
      }

      return imageUrls;
    }

    function setCardInfoImageLoading(isLoading) {
      if (!cardInfoImageWrapNode) return;

      cardInfoImageWrapNode.classList.toggle('is-loading', !!isLoading);

      if (isLoading) {
        cardInfoImageWrapNode.setAttribute('aria-busy', 'true');
      } else {
        cardInfoImageWrapNode.removeAttribute('aria-busy');
      }
    }

    function setCardInfoImage(imageUrl, title) {
      if (!cardInfoImageNode) return;

      var url = String(imageUrl || '').trim();
      cardInfoImageLoadToken += 1;
      var loadToken = cardInfoImageLoadToken;

      if (!url) {
        cardInfoImageNode.style.backgroundImage = 'none';
        cardInfoImageNode.style.setProperty('--rb-card-info-image-ratio', '1');
        if (cardInfoImageWrapNode) {
          cardInfoImageWrapNode.classList.remove('is-wide-image');
        }
        cardInfoImageNode.setAttribute('aria-label', '');
        setCardInfoImageLoading(false);
        return;
      }

      setCardInfoImageLoading(true);
      cardInfoImageNode.style.backgroundImage = 'none';
      cardInfoImageNode.setAttribute('aria-label', title || 'Product image');

      var nextImage = new Image();
      nextImage.onload = function() {
        if (loadToken !== cardInfoImageLoadToken) return;
        var width = Math.max(1, toInt(nextImage.naturalWidth, 1));
        var height = Math.max(1, toInt(nextImage.naturalHeight, 1));
        var ratio = width / height;

        if (cardInfoImageWrapNode) {
          cardInfoImageWrapNode.classList.toggle('is-wide-image', ratio > 1);
        }

        // Keep a stable container height for landscape images while preserving full-width artwork.
        cardInfoImageNode.style.setProperty('--rb-card-info-image-ratio', ratio > 1 ? '1' : String(ratio));
        cardInfoImageNode.style.backgroundImage = 'url("' + url.replace(/"/g, '\\"') + '")';
        setCardInfoImageLoading(false);
      };
      nextImage.onerror = function() {
        if (loadToken !== cardInfoImageLoadToken) return;
        cardInfoImageNode.style.setProperty('--rb-card-info-image-ratio', '1');
        if (cardInfoImageWrapNode) {
          cardInfoImageWrapNode.classList.remove('is-wide-image');
        }
        setCardInfoImageLoading(false);
      };
      nextImage.src = url;
    }

    function renderCardInfoThumbnails(imageUrls, title) {
      if (!cardInfoThumbsNode) return;

      cardInfoThumbsNode.innerHTML = '';

      if (!imageUrls || imageUrls.length <= 1) {
        cardInfoThumbsNode.hidden = true;
        return;
      }

      var fragment = document.createDocumentFragment();

      imageUrls.forEach(function(url, index) {
        var thumbButton = document.createElement('button');
        thumbButton.type = 'button';
        thumbButton.className = 'rb-card-info-modal__thumb is-loading' + (index === 0 ? ' is-active' : '');
        thumbButton.setAttribute('data-card-info-thumb', '');
        thumbButton.setAttribute('data-card-info-thumb-url', url);
        thumbButton.setAttribute('aria-label', (title || 'Product') + ' image ' + (index + 1));
        thumbButton.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
        thumbButton.setAttribute('aria-busy', 'true');

        var thumbImage = document.createElement('span');
        thumbImage.className = 'rb-card-info-modal__thumb-image';
        thumbButton.appendChild(thumbImage);

        var preloadThumb = new Image();
        preloadThumb.onload = function() {
          if (!thumbButton.isConnected) return;
          thumbImage.style.backgroundImage = 'url("' + url.replace(/"/g, '\\"') + '")';
          thumbButton.classList.remove('is-loading');
          thumbButton.removeAttribute('aria-busy');
        };
        preloadThumb.onerror = function() {
          if (!thumbButton.isConnected) return;
          thumbButton.classList.remove('is-loading');
          thumbButton.removeAttribute('aria-busy');
        };
        preloadThumb.src = url;

        fragment.appendChild(thumbButton);
      });

      cardInfoThumbsNode.appendChild(fragment);
      cardInfoThumbsNode.hidden = false;
    }

    function openCardInfoModal(card) {
      if (!cardInfoModalNode || !cardInfoTitleNode || !cardInfoDescriptionNode || !cardInfoImageWrapNode || !cardInfoImageNode || !cardInfoRulesNode) return;
      if (!card) return;

      var title = String(card.getAttribute('data-title') || '').trim();
      var description = String(card.getAttribute('data-card-description') || '').trim();
      var imageUrls = getCardInfoImageUrls(card);
      var imageUrl = imageUrls.length ? imageUrls[0] : '';

      cardInfoTitleNode.textContent = title || 'Product details';
      cardInfoDescriptionNode.textContent = description || 'No description is available for this product yet.';

      var rules = getCardQuantityRules(card);
      var ruleParts = [];

      if (rules.minOrderQty > 0) {
        ruleParts.push('Minimum qty ' + rules.minOrderQty);
      }

      if (rules.requiredQtyMultiple > 1) {
        ruleParts.push('Increments of ' + rules.requiredQtyMultiple);
      }

      if (ruleParts.length) {
        cardInfoRulesNode.hidden = false;
        cardInfoRulesNode.textContent = ruleParts.join(' • ');
      } else {
        cardInfoRulesNode.hidden = true;
        cardInfoRulesNode.textContent = '';
      }

      if (imageUrl) {
        cardInfoImageWrapNode.hidden = false;
        setCardInfoImage(imageUrl, title);
        renderCardInfoThumbnails(imageUrls, title);
      } else {
        cardInfoImageWrapNode.hidden = true;
        setCardInfoImage('', title);
        if (cardInfoThumbsNode) {
          cardInfoThumbsNode.innerHTML = '';
          cardInfoThumbsNode.hidden = true;
        }
      }

      if (cardInfoModalCloseTimer) {
        clearTimeout(cardInfoModalCloseTimer);
        cardInfoModalCloseTimer = null;
      }

      cardInfoModalNode.hidden = false;
      activateDialog(cardInfoDialogNode, card.querySelector('[data-card-info-trigger]'));
      root.classList.add('is-card-info-modal-open');
      window.requestAnimationFrame(function() {
        cardInfoModalNode.classList.add('is-open');
      });
    }

    function scrollToChooseCuts() {
      var target = builderIntroNode || root.querySelector('.rb-builder') || root;
      if (!target) return;

      var targetTop = target.getBoundingClientRect().top + window.pageYOffset;
      var extraOffset = 120;

      window.scrollTo({
        top: Math.max(0, targetTop + extraOffset),
        behavior: 'smooth'
      });
    }

    function getCards() {
      return Array.prototype.slice.call(root.querySelectorAll('.rb-card'));
    }

    function getQuantity(card) {
      var input = card.querySelector('.rb-card__qty');
      if (!input) return 0;
      var value = toInt(input.value, 0);
      return value > 0 ? value : 0;
    }

    function getSelectedVariantOption(card) {
      var select = card.querySelector('[data-variant-select]');
      if (!select) return null;
      return select.options[select.selectedIndex] || null;
    }

    function getSelectedVariant(card) {
      var select = card.querySelector('[data-variant-select]');
      var selectedOption = getSelectedVariantOption(card);

      return {
        id: String((select && select.value) || card.getAttribute('data-variant-id') || ''),
        title: String((selectedOption && selectedOption.getAttribute('data-title')) || card.getAttribute('data-variant-title') || ''),
        price: toInt((selectedOption && selectedOption.getAttribute('data-price')) || card.getAttribute('data-price'), 0),
        sellingPlanIds: String((selectedOption && selectedOption.getAttribute('data-selling-plan-ids')) || card.getAttribute('data-selling-plan-ids') || ''),
        sellingPlanPrices: String((selectedOption && selectedOption.getAttribute('data-selling-plan-prices')) || card.getAttribute('data-selling-plan-prices') || ''),
        available: selectedOption ? selectedOption.getAttribute('data-available') !== 'false' : card.getAttribute('data-unavailable') !== 'true'
      };
    }

    function isCardUnavailable(card) {
      return !getSelectedVariant(card).available;
    }

    function getCardQuantityRules(card) {
      var selectedOption = getSelectedVariantOption(card);

      return computeCardQuantityRules(
        card.getAttribute('data-product-required-qty-multiple'),
        card.getAttribute('data-product-min-order-qty'),
        selectedOption && selectedOption.getAttribute('data-required-qty-multiple'),
        selectedOption && selectedOption.getAttribute('data-min-order-qty')
      );
    }

    function applyCardQuantityRules(card) {
      var input = card.querySelector('.rb-card__qty');
      if (!input) return;

      var rules = getCardQuantityRules(card);
      var step = Math.max(1, parsePositiveInt(rules.requiredQtyMultiple));
      input.step = String(step);
      input.min = '0';
    }

    function syncCardRuleIndicator(card) {
      var indicator = card.querySelector('[data-card-quantity-rules]');
      if (!indicator) return;

      var rules = getCardQuantityRules(card);
      var parts = [];

      if (rules.minOrderQty > 0) {
        parts.push('Min ' + rules.minOrderQty);
      }

      if (rules.requiredQtyMultiple > 1) {
        parts.push('Increments of ' + rules.requiredQtyMultiple);
      }

      if (!parts.length) {
        indicator.hidden = true;
        indicator.textContent = '';
        return;
      }

      indicator.hidden = false;
      indicator.textContent = parts.join(' • ');
    }

    function syncCardVariant(card) {
      var variant = getSelectedVariant(card);
      var priceNode = card.querySelector('.rb-card__meta');
      var productTitle = String(card.getAttribute('data-title') || '');
      var baseMeta = deriveVariantMeta(productTitle, variant.title);
      var metaParts = [];

      if (priceNode) {
        metaParts.push(formatMoney(variant.price, config.moneyFormat));
        if (baseMeta) {
          metaParts.push(baseMeta);
        } else if (variant.title && variant.title !== 'Default Title') {
          metaParts.push(variant.title);
        }

        priceNode.textContent = metaParts.join(' / ');
      }

      applyCardQuantityRules(card);
      syncCardRuleIndicator(card);
      syncVariantPickerFromSelect(card);

      return variant;
    }

    function refreshCardState(card) {
      var stepper = card.querySelector('[data-stepper]');
      var qtyOutput = card.querySelector('.rb-card__qty-output');
      var qty = getQuantity(card);

      if (qtyOutput) qtyOutput.textContent = String(qty);
      card.classList.toggle('is-selected', qty > 0);

      if (!stepper) return;
      stepper.hidden = false;
    }

    function setQuantity(card, value, options) {
      var input = card.querySelector('.rb-card__qty');
      if (!input) return;

      var previousValue = Math.max(0, toInt(input.value, 0));

      var unavailable = isCardUnavailable(card);
      var rules = getCardQuantityRules(card);
      var nextValue = unavailable
        ? 0
        : normalizeCardQuantity(value, rules, options);

      nextValue = Math.max(0, nextValue);

      input.value = String(nextValue);
      refreshCardState(card);
      updateSummary();

      if (nextValue > previousValue) {
        setStatus('', false);
      }
    }

    function getPurchaseConfig() {
      return {
        type: selectedPurchaseConfig.type || 'one-time',
        label: selectedPurchaseConfig.label || 'One-time',
        planId: selectedPurchaseConfig.planId || ''
      };
    }

    function getPurchaseOptions() {
      if (!subscriptionFrequencyNode) {
        return [{ type: 'one-time', label: 'One-time', planId: '' }];
      }

      return Array.prototype.slice.call(subscriptionFrequencyNode.options)
        .map(function(option) {
          return {
            type: String(option.getAttribute('data-purchase-type') || 'one-time'),
            label: String(option.textContent || '').trim() || 'One-time',
            planId: String(option.value || ''),
            isDefault: !String(option.value || '')
          };
        })
        .filter(function(option, index, list) {
          return list.findIndex(function(entry) {
            return entry.type === option.type && entry.planId === option.planId && entry.label === option.label;
          }) === index;
        });
    }

    function setSelectedPurchaseConfig(purchaseConfig) {
      selectedPurchaseConfig = {
        type: String((purchaseConfig && purchaseConfig.type) || 'one-time'),
        label: String((purchaseConfig && purchaseConfig.label) || 'One-time'),
        planId: String((purchaseConfig && purchaseConfig.planId) || '')
      };
    }

    function getPricingRows() {
      return getCards().map(function(card) {
        var qty = getQuantity(card);
        if (!qty) return null;

        var selectedVariant = getSelectedVariant(card);
        return {
          price: selectedVariant.price,
          qty: qty,
          sellingPlanPrices: selectedVariant.sellingPlanPrices
        };
      }).filter(Boolean);
    }

    function getPricingForPlan(planId) {
      return computePricingForPlan(getPricingRows(), planId);
    }

    function closeSubscribeModal() {
      if (!subscribeModalNode) return;
      pendingSubmission = null;
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
      submitButton.classList.remove('is-loading');
      deactivateDialog(subscribeModalDialogNode);
      root.classList.remove('is-subscribe-modal-open');
      root.classList.add('is-subscribe-modal-closing');

      if (subscribeModalCloseTimer) {
        clearTimeout(subscribeModalCloseTimer);
      }

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        subscribeModalNode.hidden = true;
        root.classList.remove('is-subscribe-modal-closing');
        return;
      }

      subscribeModalCloseTimer = window.setTimeout(function() {
        subscribeModalNode.hidden = true;
        root.classList.remove('is-subscribe-modal-closing');
        subscribeModalCloseTimer = null;
      }, 320);
    }

    function openSubscribeModal() {
      if (!subscribeModalNode) return;
      if (subscribeModalCloseTimer) {
        clearTimeout(subscribeModalCloseTimer);
        subscribeModalCloseTimer = null;
      }
      setPanelOpen(false);
      subscribeModalNode.hidden = false;
      subscribeModalNode.scrollTop = 0;
      if (subscribeModalDialogNode) {
        subscribeModalDialogNode.scrollTop = 0;
      }
      root.classList.remove('is-subscribe-modal-closing');
      root.classList.add('is-subscribe-modal-open');
      activateDialog(subscribeModalDialogNode, submitButton);

      window.requestAnimationFrame(function() {
        subscribeModalNode.scrollTop = 0;
        if (subscribeModalDialogNode) {
          subscribeModalDialogNode.scrollTop = 0;
        }
      });
    }

    function renderSubscribeOptions() {
      if (!subscribeModalOptionsNode) return;

      var options = getPurchaseOptions();
      var rows = [];

      options.forEach(function(option) {
        var pricing = getPricingForPlan(option.planId);
        var isSubscription = option.type === 'subscription';
        var isActive = selectedPurchaseConfig.type === option.type && selectedPurchaseConfig.planId === option.planId;
        var disabled = isSubscription && !pricing.eligible;

        rows.push(
          '<button type="button" class="rb-subscribe-card' + (isActive ? ' is-active' : '') + '" data-builder-subscribe-option data-purchase-type="' + option.type + '" data-plan-id="' + option.planId + '" data-label="' + option.label.replace(/"/g, '&quot;') + '"' + (disabled ? ' disabled' : '') + '>' +
            '<span class="rb-subscribe-card__type">' + (isSubscription ? 'Subscription' : 'One-time buy') + '</span>' +
            '<strong class="rb-subscribe-card__label">' + option.label + '</strong>' +
            '<div class="rb-subscribe-card__price-row">' +
              (isSubscription && pricing.eligible ? '<span class="rb-subscribe-card__regular">' + formatMoney(pricing.regularTotal, config.moneyFormat) + '</span>' : '') +
              '<span class="rb-subscribe-card__discounted">' + formatMoney(pricing.optionTotal, config.moneyFormat) + '</span>' +
            '</div>' +
            (isSubscription && pricing.eligible && pricing.savings > 0
              ? '<span class="rb-subscribe-card__savings">Save ' + formatMoney(pricing.savings, config.moneyFormat) + ' on first delivery</span>'
              : (isSubscription && !pricing.eligible ? '<span class="rb-subscribe-card__savings">Unavailable for one or more selected cuts</span>' : '<span class="rb-subscribe-card__savings">No subscription</span>')) +
            '<span class="rb-subscribe-card__cta">Select</span>' +
          '</button>'
        );
      });

      subscribeModalOptionsNode.innerHTML = rows.join('');
    }

    function setSubscribeOptionLoading(activeOption) {
      if (!subscribeModalOptionsNode) return;

      var optionNodes = subscribeModalOptionsNode.querySelectorAll('[data-builder-subscribe-option]');
      Array.prototype.forEach.call(optionNodes, function(optionNode) {
        var isActive = optionNode === activeOption;
        optionNode.classList.toggle('is-loading', isActive);
        optionNode.disabled = true;
        optionNode.setAttribute('aria-busy', isActive ? 'true' : 'false');
      });
    }

    async function finalizeSubmissionWithPurchaseConfig(purchaseConfig) {
      if (!pendingSubmission) return;

      var pending = pendingSubmission;
      setSelectedPurchaseConfig(purchaseConfig);
      var latestSelections = collectSelections();

      try {
        await addBundleToCart(config, pending.rbId, latestSelections, getPurchaseConfig(), editContext);

        if (editContext && editContext.removeUpdates && Object.keys(editContext.removeUpdates).length) {
          await removeCartLinesByKeys(editContext.removeUpdates);
        }

        if (editContext && editContext.storageKey) {
          try {
            sessionStorage.removeItem(editContext.storageKey);
          } catch (e) {
            // Ignore storage cleanup failures.
          }
        }

        if (experiment) experiment.track('bundle_added');
        if (window.LobelsExperiments) {
          // Retry attribution after all Recharge cart mutations, before leaving for cart.
          // This is bounded and fail-open: analytics must never prevent a purchase.
          try { await window.LobelsExperiments.persistCartAttributes(); } catch (error) { /* Continue to cart. */ }
        }

        window.location.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart';
      } catch (error) {
        closeSubscribeModal();
        setStatus(error && error.message ? error.message : 'Could not build bundle.', true);
      } finally {
        pendingSubmission = null;
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        submitButton.classList.remove('is-loading');
      }
    }

    function getSubscriptionOptions() {
      if (!subscriptionPickerNode) return [];
      return Array.prototype.slice.call(subscriptionPickerNode.querySelectorAll('[data-subscription-option]'));
    }

    function findSubscriptionOptionByPlanId(planId) {
      var optionNodes = getSubscriptionOptions();
      if (!optionNodes.length) return null;

      var targetPlanId = String(planId || '');
      var matchingOption = optionNodes.find(function(optionNode) {
        return String(optionNode.getAttribute('data-plan-id') || '') === targetPlanId;
      });

      return matchingOption || optionNodes[0] || null;
    }

    function focusSubscriptionOptionByIndex(index) {
      var options = getSubscriptionOptions();
      if (!options.length) return;

      var safeIndex = index;
      if (safeIndex < 0) safeIndex = options.length - 1;
      if (safeIndex >= options.length) safeIndex = 0;

      options[safeIndex].focus();
    }

    function setSubscriptionOption(optionNode, settings) {
      if (!subscriptionPickerNode || !optionNode) return;

      var config = settings || {};

      Array.prototype.forEach.call(subscriptionPickerNode.querySelectorAll('[data-subscription-option]'), function(node) {
        var isActive = node === optionNode;
        node.classList.toggle('is-active', isActive);
        node.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      if (subscriptionCurrentNode) {
        subscriptionCurrentNode.textContent = String(optionNode.getAttribute('data-label') || optionNode.textContent || '').trim();
      }

      if (subscriptionFrequencyNode) {
        subscriptionFrequencyNode.value = String(optionNode.getAttribute('data-plan-id') || '');
      }

      if (!config.keepOpen) {
        setSubscriptionOpen(false);
      }

      if (subscriptionToggleNode && config.focusToggle !== false) {
        subscriptionToggleNode.focus();
      }
    }

    function setSubscriptionOpen(isOpen) {
      if (!subscriptionPickerNode || !subscriptionToggleNode || !subscriptionMenuNode) return;

      var options = getSubscriptionOptions();
      subscriptionPickerNode.classList.toggle('is-open', !!isOpen);
      subscriptionToggleNode.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      subscriptionMenuNode.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

      options.forEach(function(option) {
        option.tabIndex = isOpen ? 0 : -1;
      });
    }

    function getVariantPicker(card) {
      if (!card) return null;
      return card.querySelector('[data-variant-picker]');
    }

    function setVariantPickerOpen(card, isOpen) {
      var picker = getVariantPicker(card);
      if (!picker) return;

      var toggle = picker.querySelector('[data-variant-toggle]');
      var menu = picker.querySelector('[data-variant-menu]');
      var options = picker.querySelectorAll('[data-variant-option]');

      picker.classList.toggle('is-open', !!isOpen);
      card.classList.toggle('is-variant-open', !!isOpen);
      if (toggle) toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (menu) menu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

      Array.prototype.forEach.call(options, function(optionNode) {
        optionNode.tabIndex = isOpen ? 0 : -1;
      });
    }

    function closeAllVariantPickers(exceptCard) {
      getCards().forEach(function(card) {
        if (exceptCard && card === exceptCard) return;
        setVariantPickerOpen(card, false);
      });
    }

    function syncVariantPickerFromSelect(card) {
      var picker = getVariantPicker(card);
      if (!picker) return;

      var select = card.querySelector('[data-variant-select]');
      if (!select) return;

      var selectedValue = String(select.value || '');
      var selectedOption = select.options[select.selectedIndex] || null;
      var currentNode = picker.querySelector('[data-variant-current]');

      if (currentNode) {
        currentNode.textContent = String((selectedOption && selectedOption.textContent) || '').trim();
      }

      if (!picker.classList.contains('is-open')) {
        var toggle = picker.querySelector('[data-variant-toggle]');
        var menu = picker.querySelector('[data-variant-menu]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        if (menu) menu.setAttribute('aria-hidden', 'true');
      }

      Array.prototype.forEach.call(picker.querySelectorAll('[data-variant-option]'), function(optionNode) {
        var isActive = String(optionNode.getAttribute('data-variant-value') || '') === selectedValue;
        optionNode.classList.toggle('is-active', isActive);
        optionNode.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    }

    function getVariantOptions(card) {
      var picker = getVariantPicker(card);
      if (!picker) return [];

      return Array.prototype.filter.call(
        picker.querySelectorAll('[data-variant-option]'),
        function(optionNode) {
          return !optionNode.disabled;
        }
      );
    }

    function focusVariantOptionByIndex(card, index) {
      var options = getVariantOptions(card);
      if (!options.length) return;

      var safeIndex = index;
      if (safeIndex < 0) safeIndex = options.length - 1;
      if (safeIndex >= options.length) safeIndex = 0;

      options[safeIndex].focus();
    }

    function focusActiveVariantOption(card, direction) {
      var options = getVariantOptions(card);
      if (!options.length) return;

      var activeOption = card.querySelector('[data-variant-option].is-active');
      var activeIndex = options.indexOf(activeOption);

      if (activeIndex < 0) {
        focusVariantOptionByIndex(card, direction > 0 ? 0 : options.length - 1);
        return;
      }

      focusVariantOptionByIndex(card, activeIndex + (direction > 0 ? 1 : -1));
    }

    function applyBundleEditContext() {
      if (!editContext) return;

      if (submitButton) {
        submitButton.textContent = 'Update bundle';
      }

      var firstSelectionByProduct = new Map();
      editContext.items.forEach(function(item) {
        if (!item || !item.productId) return;
        firstSelectionByProduct.set(item.productId, item);
      });

      firstSelectionByProduct.forEach(function(item) {
        var card = findCardByProductId(item.productId);
        if (!card) return;

        var variantSelect = card.querySelector('[data-variant-select]');
        if (variantSelect && item.variantId) {
          var option = variantSelect.querySelector('option[value="' + item.variantId + '"]');
          if (option) {
            variantSelect.value = item.variantId;
            syncCardVariant(card);
          }
        }

        setQuantity(card, item.quantity, { action: 'set' });
      });

      if (editContext.sellingPlanId) {
        if (subscriptionFrequencyNode) {
          subscriptionFrequencyNode.value = editContext.sellingPlanId;
        }

        var matchingOption = findSubscriptionOptionByPlanId(editContext.sellingPlanId);
        if (matchingOption) {
          setSubscriptionOption(matchingOption, { focusToggle: false });
        }
      }
    }

    function cardMatchesFilter(card, filter) {
      if (filter === '*') return true;

      var collectionIds = String(card.getAttribute('data-collection-ids') || '');
      return ('~~' + collectionIds + '~~').indexOf('~~' + String(filter) + '~~') !== -1;
    }

    function syncTabIndicator(activeTab) {
      if (!tabsNode || !activeTab) return;

      var offsetX = Math.max(0, activeTab.offsetLeft);
      var width = Math.max(0, activeTab.offsetWidth);
      var useTextAlignedIndicator = !!(window.matchMedia && window.matchMedia('(max-width: 760px)').matches);

      if (useTextAlignedIndicator) {
        // On small screens, align to label text for scrollable tab rows.
        try {
          if (window.document && typeof window.document.createRange === 'function') {
            var range = window.document.createRange();
            range.selectNodeContents(activeTab);

            var textRect = range.getBoundingClientRect();
            if (textRect && textRect.width > 0) {
              var tabsRect = tabsNode.getBoundingClientRect();
              var textWidth = Math.max(0, textRect.width);
              var textLeft = (textRect.left - tabsRect.left) + tabsNode.scrollLeft;

              width = Math.min(width, textWidth);
              offsetX = Math.max(0, textLeft + ((textWidth - width) / 2));
            }
          }
        } catch (e) {
          // Fallback stays with tab offset metrics.
        }
      }

      tabsNode.style.setProperty('--rb-tab-indicator-x', offsetX + 'px');
      tabsNode.style.setProperty('--rb-tab-indicator-width', width + 'px');
      tabsNode.style.setProperty('--rb-tab-indicator-opacity', '1');
    }

    function revealCards(cards) {
      if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;

      cards.forEach(function(card, index) {
        if (card.__filterAnimation) {
          card.__filterAnimation.cancel();
          card.__filterAnimation = null;
        }

        card.hidden = false;
        card.style.opacity = '0';
        card.style.transform = 'translateY(15px)';

        window.requestAnimationFrame(function() {
          card.__filterAnimation = card.animate(
            [
              { opacity: 0, transform: 'translateY(15px)' },
              { opacity: 1, transform: 'translateY(0)' }
            ],
            {
              duration: 350,
              delay: index * 50,
              easing: 'ease-out',
              fill: 'both'
            }
          );

          card.__filterAnimation.onfinish = function() {
            card.style.opacity = '';
            card.style.transform = '';
            card.__filterAnimation = null;
          };

          card.__filterAnimation.oncancel = function() {
            card.style.opacity = '';
            card.style.transform = '';
            card.__filterAnimation = null;
          };
        });
      });
    }

    function selectionRows() {
      var rows = [];

      getCards().forEach(function(card) {
        var qty = getQuantity(card);
        if (!qty) return;

        var selectedVariant = getSelectedVariant(card);
        var productTitle = String(card.getAttribute('data-title') || '');

        rows.push({
          productId: String(card.getAttribute('data-product-id') || ''),
          variantId: String(selectedVariant.id || ''),
          collectionId: String(card.getAttribute('data-collection-id') || ''),
          title: productTitle || 'Item',
          variantTitle: String(selectedVariant.title || ''),
          price: toInt(selectedVariant.price, 0),
          type: String(card.getAttribute('data-type') || ''),
          qty: qty,
          meta: deriveVariantMeta(productTitle, selectedVariant.title)
        });
      });

      return rows;
    }

    function getPanelRowKey(row) {
      return String(row.productId || '') + '::' + String(row.variantId || '');
    }

    function buildPanelItemNode(row) {
      var itemNode = document.createElement('div');
      itemNode.className = 'rb-panel__item';
      itemNode.setAttribute('data-row-key', getPanelRowKey(row));

      var rowMeta = row.meta;
      if (!rowMeta && row.variantTitle && row.variantTitle !== 'Default Title') {
        rowMeta = row.variantTitle;
      }

      var itemTopNode = document.createElement('div');
      itemTopNode.className = 'rb-panel__item-top';

      var copyNode = document.createElement('div');
      var titleNode = document.createElement('h4');
      titleNode.textContent = row.title;
      copyNode.appendChild(titleNode);

      if (rowMeta) {
        var metaNode = document.createElement('p');
        metaNode.textContent = rowMeta;
        copyNode.appendChild(metaNode);
      }

      var removeButton = document.createElement('button');
      removeButton.className = 'rb-panel__remove';
      removeButton.type = 'button';
      removeButton.setAttribute('data-action', 'remove');
      removeButton.setAttribute('data-product-id', row.productId);
      removeButton.setAttribute('aria-label', 'Remove ' + row.title);
      removeButton.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12"></path><path d="M18 6l-12 12"></path></svg>';

      itemTopNode.appendChild(copyNode);
      itemTopNode.appendChild(removeButton);

      var controlsNode = document.createElement('div');
      controlsNode.className = 'rb-panel__item-controls';

      var stepperNode = document.createElement('div');
      stepperNode.className = 'rb-card__stepper';

      var decreaseButton = document.createElement('button');
      decreaseButton.type = 'button';
      decreaseButton.className = 'rb-card__step';
      decreaseButton.setAttribute('data-action', 'decrease');
      decreaseButton.setAttribute('data-product-id', row.productId);
      decreaseButton.setAttribute('aria-label', 'Decrease quantity for ' + row.title);
      decreaseButton.innerHTML = '<svg class="rb-card__step-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"></path></svg>';

      var qtyOutput = document.createElement('span');
      qtyOutput.className = 'rb-card__qty-output';
      qtyOutput.textContent = String(row.qty);

      var increaseButton = document.createElement('button');
      increaseButton.type = 'button';
      increaseButton.className = 'rb-card__step';
      increaseButton.setAttribute('data-action', 'increase');
      increaseButton.setAttribute('data-product-id', row.productId);
      increaseButton.setAttribute('aria-label', 'Increase quantity for ' + row.title);
      increaseButton.innerHTML = '<svg class="rb-card__step-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14"></path><path d="M5 12h14"></path></svg>';

      stepperNode.appendChild(decreaseButton);
      stepperNode.appendChild(qtyOutput);
      stepperNode.appendChild(increaseButton);

      var priceNode = document.createElement('span');
      priceNode.className = 'rb-panel__item-price';
      priceNode.textContent = formatMoney(row.price * row.qty, config.moneyFormat);

      controlsNode.appendChild(stepperNode);
      controlsNode.appendChild(priceNode);

      itemNode.appendChild(itemTopNode);
      itemNode.appendChild(controlsNode);

      return itemNode;
    }

    function animatePanelItemIn(node) {
      node.style.transition = '';
      node.style.maxHeight = '';
      node.style.opacity = '';
      node.style.transform = '';
      node.style.overflow = '';
      node.style.marginTop = '';
      node.style.marginBottom = '';
      node.style.paddingTop = '';
      node.style.paddingBottom = '';
    }

    function animatePanelItemOut(node, onDone) {
      node.style.transition = '';
      node.style.maxHeight = '';
      node.style.opacity = '';
      node.style.transform = '';
      node.style.overflow = '';
      node.style.marginTop = '';
      node.style.marginBottom = '';
      node.style.paddingTop = '';
      node.style.paddingBottom = '';
      onDone();
    }

    function createEmptyStateNode() {
      var emptyNode = document.createElement('p');
      emptyNode.className = 'rb-panel__empty';
      emptyNode.textContent = 'Your box is empty.';
      return emptyNode;
    }

    function showEmptyState(animate) {
      if (boxItemsNode.querySelector('.rb-panel__empty')) return;

      var emptyNode = createEmptyStateNode();
      boxItemsNode.appendChild(emptyNode);
    }

    function renderBox(rows) {
      var control = boxItemsNode.contains(document.activeElement) ? document.activeElement : null;
      var row = control && control.closest('[data-row-key]');
      var key = row && row.getAttribute('data-row-key');
      var action = control && control.getAttribute('data-action');
      var index = row ? Array.prototype.indexOf.call(boxItemsNode.children, row) : -1;
      renderBoxContents(rows);
      if (!row) return;
      var items = Array.prototype.slice.call(boxItemsNode.querySelectorAll('[data-row-key]'));
      var replacementRow = items.find(function(item) { return item.getAttribute('data-row-key') === key; }) || items[Math.min(index, items.length - 1)];
      var target = replacementRow && (replacementRow.querySelector('[data-action="' + action + '"]') || replacementRow.querySelector('button'));
      if (!target) target = window.innerWidth <= 980 ? panelCloseNode : submitButton;
      if (target) target.focus({ preventScroll: true });
    }

    function renderBoxContents(rows) {
      if (!rows.length) {
        var existingItems = Array.prototype.slice.call(boxItemsNode.querySelectorAll('.rb-panel__item'));
        if (existingItems.length) {
          existingItems.forEach(function(itemNode) {
            animatePanelItemOut(itemNode, function() {
              itemNode.remove();
              if (!boxItemsNode.querySelector('.rb-panel__item') && !boxItemsNode.querySelector('.rb-panel__empty')) {
                showEmptyState(true);
              }
            });
          });
        } else {
          showEmptyState(true);
        }
        return;
      }

      var emptyNode = boxItemsNode.querySelector('.rb-panel__empty');
      if (emptyNode) {
        emptyNode.remove();
      }

      var existingNodes = Array.prototype.slice.call(boxItemsNode.querySelectorAll('.rb-panel__item'));
      var existingMap = new Map();
      existingNodes.forEach(function(node) {
        existingMap.set(String(node.getAttribute('data-row-key') || ''), node);
      });

      var nextKeys = new Set();
      var orderedNodes = [];

      rows.forEach(function(row) {
        var rowKey = getPanelRowKey(row);
        nextKeys.add(rowKey);

        var existingNode = existingMap.get(rowKey);
        if (existingNode) {
          var refreshedNode = buildPanelItemNode(row);
          existingNode.innerHTML = refreshedNode.innerHTML;
          existingNode.setAttribute('data-row-key', rowKey);
          existingNode.style.transition = '';
          existingNode.style.maxHeight = '';
          existingNode.style.opacity = '';
          existingNode.style.transform = '';
          existingNode.style.overflow = '';
          existingNode.style.marginTop = '';
          existingNode.style.marginBottom = '';
          existingNode.style.paddingTop = '';
          existingNode.style.paddingBottom = '';
          orderedNodes.push(existingNode);
          return;
        }

        var newNode = buildPanelItemNode(row);
        orderedNodes.push(newNode);
      });

      orderedNodes.forEach(function(node) {
        if (!node.parentNode) {
          boxItemsNode.appendChild(node);
          animatePanelItemIn(node);
        } else {
          boxItemsNode.appendChild(node);
        }
      });

      existingNodes.forEach(function(existingNode) {
        var key = String(existingNode.getAttribute('data-row-key') || '');
        if (nextKeys.has(key)) return;

        animatePanelItemOut(existingNode, function() {
          existingNode.remove();
          if (!boxItemsNode.querySelector('.rb-panel__item') && !boxItemsNode.querySelector('.rb-panel__empty')) {
            showEmptyState(true);
          }
        });
      });
    }

    function setText(nodeList, text) {
      Array.prototype.forEach.call(nodeList, function(node) { node.textContent = text; });
    }

    function setWidth(nodeList, percent) {
      Array.prototype.forEach.call(nodeList, function(node) { node.style.width = percent + '%'; });
    }

    function renderSummary(summary, rows) {
      var itemWord = summary.itemCount === 1 ? 'item' : 'items';
      setText(itemLabelNodes, summary.itemCount + ' ' + itemWord + ' in your box');
      setText(panelCountNodes, String(summary.itemCount));
      setText(freeShippingThresholdNodes, formatMoney(summary.shippingThreshold, config.moneyFormat));
      setText(subtotalNodes, formatMoney(summary.effectiveSubtotal, config.moneyFormat));

      if (discountCopyNode) {
        discountCopyNode.hidden = !summary.discountCopy;
        discountCopyNode.textContent = summary.discountCopy;
      }

      if (remainingNode) {
        remainingNode.textContent = formatMoney(summary.effectiveSubtotal, config.moneyFormat);
      }

      if (panelShippingCurrentNode) {
        panelShippingCurrentNode.textContent = formatMoney(summary.effectiveSubtotal, config.moneyFormat);
      }

      if (topProgressCopyNode) {
        topProgressCopyNode.textContent = summary.progressCopy;
      }

      if (panelProgressCopyNode) {
        panelProgressCopyNode.textContent = summary.shippingRemaining === 0 ? 'Free shipping unlocked.' : (summary.progressCopy + '.');
      }

      setWidth([topFillNode, panelFillNode].filter(Boolean), summary.shippingProgress);

      if (titleNode) {
        titleNode.textContent = summary.titleCopy;
      }

      renderBox(rows);
      submitButton.classList.toggle('is-disabled', !summary.meetsMinimum);
      submitButton.setAttribute('aria-disabled', summary.meetsMinimum ? 'false' : 'true');

      if (!submitButton.classList.contains('is-loading')) {
        submitButton.disabled = false;
      }
    }

    function updateSummary() {
      var rows = selectionRows();
      var purchaseConfig = getPurchaseConfig();
      var summary = computeSummary({
        rows: rows,
        minimumItems: config.minimumItems,
        freeShippingThresholdCents: config.freeShippingThresholdCents,
        savingsPercent: config.savingsPercent,
        isSubscription: purchaseConfig.type === 'subscription',
        moneyFormat: config.moneyFormat
      });

      renderSummary(summary, rows);
      var summaryAnnouncement = summary.itemCount + (summary.itemCount === 1 ? ' item' : ' items') + ' in your box. Subtotal ' + formatMoney(summary.effectiveSubtotal, config.moneyFormat) + '. ' + summary.progressCopy;
      if (lastSummaryAnnouncement !== null && summaryAnnouncement !== lastSummaryAnnouncement) announce(summaryAnnouncement);
      lastSummaryAnnouncement = summaryAnnouncement;
    }

    function applyFilter(tab) {
      if (!tab || tab.classList.contains('is-active')) return;

      var filter = tab.getAttribute('data-filter');

      Array.prototype.forEach.call(root.querySelectorAll('.rb-tab'), function(t) {
        t.classList.toggle('is-active', t === tab);
        t.setAttribute('aria-pressed', t === tab ? 'true' : 'false');
      });

      syncTabIndicator(tab);

      root.classList.add('is-filtering');

      if (root.__filterTimer) {
        clearTimeout(root.__filterTimer);
      }

      var matchedCards = [];

      getCards().forEach(function(card) {
        if (cardMatchesFilter(card, filter)) {
          matchedCards.push(card);
        } else {
          if (card.__filterAnimation) {
            card.__filterAnimation.cancel();
            card.__filterAnimation = null;
          }
          card.hidden = true;
          card.style.opacity = '';
          card.style.transform = '';
        }
      });

      revealCards(matchedCards);
      announce(tab.textContent.trim() + ': ' + matchedCards.length + ' products shown.');

      root.__filterTimer = window.setTimeout(function() {
        root.classList.remove('is-filtering');
        root.__filterTimer = null;
      }, 420);
    }

    function findCardByProductId(productId) {
      if (!productId) return null;
      return root.querySelector('.rb-card[data-product-id="' + String(productId) + '"]');
    }

    function collectSelections() {
      var selections = [];
      var purchaseConfig = getPurchaseConfig();
      var selectedPlanId = purchaseConfig && purchaseConfig.planId ? String(purchaseConfig.planId) : '';

      getCards().forEach(function(card) {
        var qty = getQuantity(card);
        if (!qty) return;

        var selectedVariant = getSelectedVariant(card);

        selections.push({
          collectionId: String(card.getAttribute('data-collection-id') || ''),
          externalProductId: String(card.getAttribute('data-product-id') || ''),
          externalVariantId: String(selectedVariant.id || ''),
          supportsSellingPlan: variantSupportsSellingPlan(selectedVariant.sellingPlanIds, selectedPlanId),
          quantity: qty,
          title: String(card.getAttribute('data-title') || 'Item'),
          variantTitle: String(selectedVariant.title || '')
        });
      });

      return selections;
    }

    function summarizeSelections(selections) {
      return selections.map(function(selection) {
        var variantTitle = selection.variantTitle;
        return selection.quantity + ' x ' + selection.title + (variantTitle && variantTitle !== 'Default Title' ? ' (' + variantTitle + ')' : '');
      }).join('\n');
    }

    async function handleSubmit() {
      setStatus('', false);

      if (!config.bundleProductId || !config.bundleVariantId) {
        setStatus('Set a bundle product in the section settings.', true);
        return;
      }

      if (!config.bundleHandle) {
        setStatus('Bundle handle is missing. Set the bundle product in this section.', true);
        return;
      }

      var selections = collectSelections();
      var selectedCount = selections.reduce(function(total, selection) {
        return total + selection.quantity;
      }, 0);

      if (selectedCount < config.minimumItems) {
        setStatus('Please select at least ' + config.minimumItems + ' items.', true);
        return;
      }

      var missingCollectionId = selections.some(function(selection) {
        return !selection.collectionId;
      });

      if (missingCollectionId) {
        setStatus('Some selected items are missing a collection ID. Set a source collection in section settings.', true);
        return;
      }

      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.classList.add('is-loading');

      try {
        await loadRechargeSdk();

        var methods = getRechargeBundleMethods();
        if (!methods) {
          throw new Error('Recharge SDK bundle methods were not found on this page.');
        }

        await initRecharge(methods.client, config.storeIdentifier);

        var bundle = {
          externalProductId: String(config.bundleProductId),
          externalVariantId: String(config.bundleVariantId),
          selections: selections
        };

        var validationResult = await methods.validateBundle(bundle);
        if (validationResult !== true) {
          throw new Error(typeof validationResult === 'string' ? validationResult : 'Bundle validation failed.');
        }

        var rbId = await methods.getBundleId(bundle);
        if (!rbId) {
          throw new Error('Recharge did not return a bundle id.');
        }

        if (editContext && editContext.removeUpdates && Object.keys(editContext.removeUpdates).length) {
          // Removal is deferred until a purchase option is selected.
        }

        var summary = summarizeSelections(selections);
        pendingSubmission = {
          rbId: rbId,
          selections: selections,
          summary: summary
        };

        renderSubscribeOptions();
        openSubscribeModal();
      } catch (error) {
        setStatus(error && error.message ? error.message : 'Could not build bundle.', true);
      } finally {
        if (!pendingSubmission) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
          submitButton.classList.remove('is-loading');
        }
      }
    }

    // Click handling is delegated through a single listener on root (see bottom of this
    // block), but registered here as one rule per widget so each interaction reads as its
    // own small handler. Rules are matched in registration order, first match wins — order
    // matters (e.g. the generic [data-action] fallback must stay last).
    var clickRules = [];
    function onClick(selector, handle) {
      clickRules.push({ selector: selector, handle: handle });
    }

    // Panel
    onClick('[data-builder-panel-toggle]', function() {
      setPanelOpen(!root.classList.contains('is-panel-open'));
    });
    onClick('[data-builder-panel-close]', function() {
      setPanelOpen(false);
    });
    onClick('[data-builder-panel-backdrop]', function() {
      setPanelOpen(false);
    });

    // Promotion details popout
    onClick('[data-promo-open]', function() {
      setPromoDetailsOpen(true);
      if (experiment) experiment.track('promotion_opened');
    });
    onClick('[data-promo-close], [data-promo-collapse]', function() {
      setPromoDetailsOpen(false);
    });

    // Tabs
    onClick('.rb-tab', function(tab) {
      applyFilter(tab);
    });

    // Submit
    onClick('[data-builder-submit]', function() {
      handleSubmit();
    });

    // Card info modal
    onClick('[data-card-info-trigger]', function(trigger) {
      openCardInfoModal(trigger.closest('.rb-card'));
    });
    onClick('[data-card-info-close], [data-card-info-backdrop]', function() {
      closeCardInfoModal();
    });
    onClick('[data-card-info-thumb]', function(thumb) {
      if (thumb.classList.contains('is-active')) return;

      var thumbUrl = String(thumb.getAttribute('data-card-info-thumb-url') || '').trim();
      if (thumbUrl) {
        var modalTitle = String((cardInfoTitleNode && cardInfoTitleNode.textContent) || '').trim();
        setCardInfoImage(thumbUrl, modalTitle);
      }

      if (cardInfoThumbsNode) {
        Array.prototype.forEach.call(cardInfoThumbsNode.querySelectorAll('[data-card-info-thumb]'), function(node) {
          var isActive = node === thumb;
          node.classList.toggle('is-active', isActive);
          node.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }
    });

    // Subscribe modal
    onClick('[data-builder-subscribe-close], [data-builder-subscribe-backdrop]', function() {
      closeSubscribeModal();
    });
    onClick('[data-builder-subscribe-option]', function(option) {
      if (option.disabled) return;
      if (experiment) experiment.track(option.getAttribute('data-purchase-type') === 'subscription' ? 'subscription_selected' : 'one_time_selected');

      setSubscribeOptionLoading(option);

      finalizeSubmissionWithPurchaseConfig({
        type: String(option.getAttribute('data-purchase-type') || 'one-time'),
        planId: String(option.getAttribute('data-plan-id') || ''),
        label: String(option.getAttribute('data-label') || option.textContent || 'One-time').trim()
      });
    });

    // Subscription picker
    onClick('[data-subscription-toggle]', function() {
      if (!subscriptionPickerNode || !subscriptionMenuNode || !subscriptionToggleNode) return;
      setSubscriptionOpen(!subscriptionPickerNode.classList.contains('is-open'));
    });
    onClick('[data-subscription-option]', function(option) {
      setSubscriptionOption(option);
      updateSummary();
    });

    // Variant picker
    onClick('[data-variant-toggle]', function(toggle) {
      var variantCardForToggle = toggle.closest('.rb-card');
      if (!variantCardForToggle) return;

      var picker = getVariantPicker(variantCardForToggle);
      if (!picker) return;

      var isOpen = picker.classList.contains('is-open');
      closeAllVariantPickers(variantCardForToggle);
      setVariantPickerOpen(variantCardForToggle, !isOpen);
    });
    onClick('[data-variant-option]', function(optionNode) {
      if (optionNode.disabled) return;

      var variantCard = optionNode.closest('.rb-card');
      if (!variantCard) return;

      var variantSelectNode = variantCard.querySelector('[data-variant-select]');
      if (!variantSelectNode) return;

      variantSelectNode.value = String(optionNode.getAttribute('data-variant-value') || '');
      variantSelectNode.dispatchEvent(new Event('change', { bubbles: true }));
      setVariantPickerOpen(variantCard, false);
    });

    // Quantity actions
    onClick('.rb-card__media-wrap', function(mediaNode) {
      var mediaCard = mediaNode.closest('.rb-card');
      if (!mediaCard) return;

      if (!isCardUnavailable(mediaCard)) {
        setQuantity(mediaCard, getQuantity(mediaCard), { action: 'increase' });
      }
    });
    onClick('[data-action]', function(actionNode) {
      var action = actionNode.getAttribute('data-action');
      var card = actionNode.closest('.rb-card');
      var productId = actionNode.getAttribute('data-product-id');
      if (!card && productId) card = findCardByProductId(productId);
      if (!card) return;

      var currentValue = getQuantity(card);
      if (action === 'add' || action === 'increase') setQuantity(card, currentValue, { action: 'increase' });
      if (action === 'decrease') setQuantity(card, currentValue, { action: 'decrease' });
      if (action === 'remove') setQuantity(card, 0, { action: 'remove' });
    });

    root.addEventListener('click', function(event) {
      for (var i = 0; i < clickRules.length; i++) {
        var match = event.target.closest(clickRules[i].selector);
        if (match) {
          clickRules[i].handle(match, event);
          return;
        }
      }
    });

    root.addEventListener('change', function(event) {
      if (event.target.matches('[data-variant-select]')) {
        var variantCard = event.target.closest('.rb-card');
        if (!variantCard) return;

        syncCardVariant(variantCard);
        setQuantity(variantCard, getQuantity(variantCard), { action: 'set' });
        return;
      }

      if (event.target.matches('[data-builder-subscription-frequency]')) {
        var optionFromSelect = event.target.options[event.target.selectedIndex];
        var selectedPlanId = optionFromSelect ? String(optionFromSelect.value || '') : '';
        var matchingPickerOption = findSubscriptionOptionByPlanId(selectedPlanId);

        if (matchingPickerOption) {
          setSubscriptionOption(matchingPickerOption, { focusToggle: false });
        }

        updateSummary();
        return;
      }

      if (!event.target.classList.contains('rb-card__qty')) return;

      var card = event.target.closest('.rb-card');
      if (!card) return;

      setQuantity(card, toInt(event.target.value, 0), { action: 'input' });
    });

    // Each widget's keyboard handling lives in its own function, returning true once it has
    // fully handled (or determined it owns, but ignores) the keydown, so the dispatcher below
    // can stop at the first one that claims the event — the same short-circuiting the single
    // if/else chain used to do.
    function handleCardInfoModalKeydown(event) {
      if (event.key === 'Escape' && root.classList.contains('is-card-info-modal-open')) {
        closeCardInfoModal();
        return true;
      }

      return false;
    }

    function handleSubscriptionPickerKeydown(event) {
      if (!subscriptionPickerNode || !subscriptionToggleNode || !subscriptionMenuNode) return false;

      var focusedToggle = event.target.closest('[data-subscription-toggle]');
      var focusedOption = event.target.closest('[data-subscription-option]');
      var isSubscriptionOpen = subscriptionPickerNode.classList.contains('is-open');

      if (focusedToggle) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          setSubscriptionOpen(true);

          var activeOption = subscriptionPickerNode.querySelector('[data-subscription-option].is-active');
          var options = getSubscriptionOptions();
          var activeIndex = options.indexOf(activeOption);
          if (activeIndex < 0) activeIndex = 0;
          focusSubscriptionOptionByIndex(activeIndex + (event.key === 'ArrowDown' ? 1 : -1));
        }

        if (event.key === 'Escape' && isSubscriptionOpen) {
          event.preventDefault();
          setSubscriptionOpen(false);
        }

        return true;
      }

      if (focusedOption) {
        var optionList = getSubscriptionOptions();
        var currentIndex = optionList.indexOf(focusedOption);

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          focusSubscriptionOptionByIndex(currentIndex + 1);
          return true;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          focusSubscriptionOptionByIndex(currentIndex - 1);
          return true;
        }

        if (event.key === 'Home') {
          event.preventDefault();
          focusSubscriptionOptionByIndex(0);
          return true;
        }

        if (event.key === 'End') {
          event.preventDefault();
          focusSubscriptionOptionByIndex(optionList.length - 1);
          return true;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          setSubscriptionOpen(false);
          if (subscriptionToggleNode) {
            subscriptionToggleNode.focus();
          }
          return true;
        }
      }

      return false;
    }

    function handleVariantPickerKeydown(event) {
      var focusedToggle = event.target.closest('[data-variant-toggle]');
      var focusedOption = event.target.closest('[data-variant-option]');

      if (focusedToggle) {
        var variantCardForToggle = focusedToggle.closest('.rb-card');
        if (!variantCardForToggle) return true;

        var variantPicker = getVariantPicker(variantCardForToggle);
        var isVariantOpen = !!(variantPicker && variantPicker.classList.contains('is-open'));

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          closeAllVariantPickers(variantCardForToggle);
          setVariantPickerOpen(variantCardForToggle, true);
          focusActiveVariantOption(variantCardForToggle, event.key === 'ArrowDown' ? 1 : -1);
          return true;
        }

        if (event.key === 'Escape' && isVariantOpen) {
          event.preventDefault();
          setVariantPickerOpen(variantCardForToggle, false);
          return true;
        }

        return true;
      }

      if (focusedOption) {
        var variantCardForOption = focusedOption.closest('.rb-card');
        if (!variantCardForOption) return true;

        var variantOptions = getVariantOptions(variantCardForOption);
        var optionIndex = variantOptions.indexOf(focusedOption);

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          focusVariantOptionByIndex(variantCardForOption, optionIndex + 1);
          return true;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          focusVariantOptionByIndex(variantCardForOption, optionIndex - 1);
          return true;
        }

        if (event.key === 'Home') {
          event.preventDefault();
          focusVariantOptionByIndex(variantCardForOption, 0);
          return true;
        }

        if (event.key === 'End') {
          event.preventDefault();
          focusVariantOptionByIndex(variantCardForOption, variantOptions.length - 1);
          return true;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          setVariantPickerOpen(variantCardForOption, false);

          var variantToggle = variantCardForOption.querySelector('[data-variant-toggle]');
          if (variantToggle) {
            variantToggle.focus();
          }
        }

        return true;
      }

      return false;
    }

    root.addEventListener('keydown', function(event) {
      if (activeDialog) {
        if (event.key === 'Escape') {
          event.preventDefault();
          if (activeDialog === cardInfoDialogNode) closeCardInfoModal();
          else closeSubscribeModal();
          return;
        }
        if (event.key === 'Tab') {
          var options = focusableNodes(activeDialog);
          var first = options[0] || activeDialog;
          var last = options[options.length - 1] || activeDialog;
          if (!options.length || !options.includes(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus({ preventScroll: true });
          }
          return;
        }
      }
      if (event.key === 'Escape' && (root.classList.contains('is-promo-details-open') || root.classList.contains('is-promo-details-intro'))) {
        setPromoDetailsOpen(false);
      }

      if (handleCardInfoModalKeydown(event)) return;
      if (handleSubscriptionPickerKeydown(event)) return;
      if (handleVariantPickerKeydown(event)) return;
      if (event.key === 'Escape' && root.classList.contains('is-panel-open')) {
        event.preventDefault();
        setPanelOpen(false);
      }
    });

    document.addEventListener('click', function(event) {
      if (!subscriptionPickerNode || !subscriptionToggleNode || !subscriptionMenuNode) return;
      if (!subscriptionPickerNode.classList.contains('is-open')) return;
      if (subscriptionPickerNode.contains(event.target)) return;
      setSubscriptionOpen(false);
    });

    document.addEventListener('click', function(event) {
      if (event.target.closest('[data-variant-picker]')) return;
      closeAllVariantPickers();
    });

    if (subscriptionPickerNode) {
      var initialOption = subscriptionPickerNode.querySelector('[data-subscription-option].is-active') || subscriptionPickerNode.querySelector('[data-subscription-option]');
      if (initialOption) {
        setSubscriptionOption(initialOption, { focusToggle: false });
        setSelectedPurchaseConfig({
          type: String(initialOption.getAttribute('data-purchase-type') || 'one-time'),
          label: String(initialOption.getAttribute('data-label') || initialOption.textContent || 'One-time').trim(),
          planId: String(initialOption.getAttribute('data-plan-id') || '')
        });
      }
      setSubscriptionOpen(false);
    }

    var initialActiveTab = tabsNode && !tabsNode.closest('[hidden]')
      ? (root.querySelector('.rb-tab.is-active') || root.querySelector('.rb-tab')) : null;
    if (initialActiveTab) {
      Array.prototype.forEach.call(root.querySelectorAll('.rb-tab'), function(tab) {
        tab.setAttribute('aria-pressed', tab === initialActiveTab ? 'true' : 'false');
      });
      syncTabIndicator(initialActiveTab);

      var initialFilter = initialActiveTab.getAttribute('data-filter');
      getCards().forEach(function(card) {
        if (!cardMatchesFilter(card, initialFilter)) {
          card.hidden = true;
        }
      });
    }

    getCards().forEach(function(card) {
      syncCardVariant(card);
      refreshCardState(card);
    });

    bindCardImageLoadingStates();
    syncFirstPromoTruckIcon();

    applyBundleEditContext();

    updateSummary();
    setPanelOpen(false);
    setPromoDetailsOpen(false);

    if (promoPanelNode && promoOpenNodes.length && !(editContext && editContext.isEditMode)) {
      Array.prototype.forEach.call(promoOpenNodes, function(node) {
        node.addEventListener('focusin', function() {
          if (node.matches(':focus-visible')) cancelPromoIntro();
        });
        node.addEventListener('focusout', function() {
          startPromoIntro(12000);
        });
      });
      root.addEventListener('animationend', function(event) {
        var isRevealing = root.classList.contains('is-promo-details-revealing');
        if (event.animationName === (isRevealing ? 'rb-promo-details-reveal' : 'rb-promo-details-shine')) {
          finishPromoIntro();
        }
      });
      function refreshPromoShine() {
        if (!canShinePromoDetails()) cancelPromoIntro();
        else startPromoIntro(12000);
      }
      window.addEventListener('resize', refreshPromoShine);
      document.addEventListener('visibilitychange', refreshPromoShine);
      if (window.matchMedia) {
        window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', refreshPromoShine);
      }
      function enablePromoShine() {
        promoIntroReady = true;
        startPromoIntro(600, !experiment || experiment.behavior === 'unchanged' || experiment.behavior === 'peek');
      }
      if (document.readyState === 'complete') {
        enablePromoShine();
      } else {
        window.addEventListener('load', enablePromoShine, { once: true });
      }
    }

    if (hasUrlSelectionItems()) {
      window.requestAnimationFrame(function() {
        window.setTimeout(scrollToChooseCuts, 120);
      });
    }

    window.addEventListener('resize', function() {
      if (!root.isConnected) return;
      var activeTab = root.querySelector('.rb-tab.is-active') || root.querySelector('.rb-tab');
      if (activeTab) {
        syncTabIndicator(activeTab);
      }

      if (!activeDialog && window.innerWidth > 980) {
        setPanelOpen(false);
      } else if (!activeDialog) {
        setPanelOpen(root.classList.contains('is-panel-open'));
      }

      closeAllVariantPickers();
    });
  }

  function bootstrap() {
    var builders = document.querySelectorAll('.recharge-builder');
    builders.forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
  document.addEventListener('shopify:section:load', bootstrap);
})();
